/**
 * Interwork Marketing — English
 */
import type { Dictionary } from "../types";

export const en: Dictionary = {
  common: {
    loading: "Loading...",
    error: "Something went wrong",
    retry: "Retry",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    confirm: "Confirm",
    search: "Search",
    filter: "Filter",
    filters: "Filters",
    export: "Export CSV",
    refresh: "Refresh",
    back: "Back",
    next: "Next",
    previous: "Previous",
    submit: "Submit",
    actions: "Actions",
    yes: "Yes",
    no: "No",
    empty: "No data",
    all: "All",
    active: "Active",
    paused: "Paused",
    archived: "Archived",
    total: "Total",
    today: "Today",
    yesterday: "Yesterday",
    week: "Week",
    month: "Month",
    last7: "Last 7 days",
    last30: "Last 30 days",
    last90: "Last 90 days",
    overall: "Overall",
    settings: "Settings",
    logout: "Log out",
    language: "Language",
    languageUz: "Uzbek",
    languageRu: "Russian",
    languageEn: "English",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    popular: "Popular",
    more: "More",
    noData: "No data",
    sync: "Sync",
    syncStarted: "Sync started",
    details: "Details",
    status: "Status",
    name: "Name",
    date: "Date",
    period: "Period",
  },
  nav: {
    home: "Home",
    sales: "Sales",
    marketing: "Marketing",
    chatbot: "Chatbot",
    coach: "Coach",
    settings: "Settings",
    navigation: "Navigation",
    salesData: "Sales data",
    company: "Company",
    integration: "Integrations",
    analysis: "Analytics",
    knowledge: "Knowledge",
  },
  auth: {
    login: "Sign in",
    loggingIn: "Signing in...",
    username: "Username",
    password: "Password",
    usernameRequired: "Enter username",
    passwordMin: "Password must be at least 8 characters",
    loginError: "Sign-in failed. Please try again.",
    showPassword: "Show password",
    hidePassword: "Hide password",
    welcome: "Welcome",
    subtitle: "Sign in to Interwork Marketing",
  },
  marketing: {
    module: "Marketing",
    nav: {
      dashboard: "Dashboard",
      campaigns: "Campaigns",
      creatives: "Creatives",
      scenarios: "Scenarios",
      competitors: "Competitors",
      stories: "Stories",
      clients: "Clients",
      summaries: "Summaries",
      adAccounts: "Ad accounts",
    },
    dashboard: {
      title: "Dashboard",
      breadcrumb: "Marketing",
      emptyTitle: "No data",
      emptyDesc: "No metrics for this period. Sync your ad account.",
      spend: "Spend",
      leads: "Leads",
      cpl: "CPL",
      roas: "ROAS",
      impressions: "Impressions",
      clicks: "Clicks",
      ctr: "CTR",
      sync: "Sync",
    },
    campaigns: {
      title: "Campaigns",
      description: "Meta spend + CRM (SalesLead · fb ad tags)",
      searchPlaceholder: "Campaign name...",
      notFound: "No campaigns found",
      notFoundDesc: "Change filters or wait for sync",
      noActive: "No active campaigns",
      noActiveDesc: "There are no active campaigns yet",
      activeSection: "Active campaigns",
      inactiveSection: "Paused / archived",
      wonDynamics: "Won dynamics — by day",
      campaignsCount: "{{count}} campaigns · Meta spend + CRM",
      col: {
        campaign: "Campaign",
        spend: "Spend",
        leads: "Leads",
        cpl: "CPL",
        act: "Act",
        won: "Won",
        lost: "Lost",
        qual: "Qual",
        costWon: "Cost/Won",
        convRate: "Conv.rate",
        qualRate: "Qual.rate",
        dealTime: "Deal time",
      },
      totalRow: "Total ({{count}} campaigns)",
      back: "Campaigns",
      overview: "Overview",
      creativesTab: "Creatives",
      summaryTab: "Summary",
    },
    creatives: {
      title: "Creatives",
      description: "Ad library — active ads, creatives and statuses",
      lastSync: "last sync",
      filters: "Filters",
      results: "~{{count}} results",
      empty: "No creatives found",
      emptyDesc: "Change filters or period",
      allCampaigns: "All campaigns",
    },
    competitors: {
      title: "Competitors",
      description: "Instagram competitors: benchmark, engagement and AI analysis",
      add: "Add competitor",
      addFirst: "Add your first competitor",
      addFirstDesc:
        "Enter a competitor Instagram profile — we prepare benchmark, top reels and AI analysis.",
      emptyCompare: "No competitors to compare",
      emptyCompareDesc: "Add a competitor first to see us-vs-them analysis.",
      notConfigured: "Competitor analysis not configured",
      notConfiguredDesc:
        "Apify is not connected for Instagram data. Contact your administrator.",
      own: "Our account",
      scrape: "Refresh",
      deepScrape: "Deep scrape",
      followers: "Followers",
      posts: "Posts",
      engagement: "Engagement",
      avgViews: "Avg. views",
      videoAnalysis: "Video AI analysis",
      compare: "Compare",
      username: "Instagram username",
      displayName: "Display name",
      isOwn: "This is our account",
    },
    stories: {
      title: "Stories archive",
      description: "Save and browse Instagram stories",
    },
    scenarios: {
      title: "Scenarios",
      description: "AI-generated ad scenarios",
    },
    clients: {
      title: "Clients",
      description: "Sales CRM clients and segments",
      search: "Search clients...",
      empty: "No clients found",
    },
    summaries: {
      title: "Summaries",
      description: "Sales AI summaries",
    },
    status: {
      ACTIVE: "Active",
      PAUSED: "Paused",
      ARCHIVED: "Archived",
      DELETED: "Deleted",
    },
  },
  period: {
    today: "Today",
    week: "Week",
    month: "Month",
    overall: "Overall",
    custom: "Custom",
  },
  legal: {
    privacy: {
      title: "Privacy Policy",
      lastUpdated: "Last updated: 2026-05-07",
    },
    terms: {
      title: "Terms of Service",
      lastUpdated: "Last updated: 2026-05-07",
    },
    deletion: {
      title: "Data deletion request",
      description:
        "Submit a request to delete data collected via Meta platform integrations.",
      submit: "Submit request",
      success: "Request received",
    },
  },
  chatbot: {
    nav: {
      dashboard: "Dashboard",
      inbox: "Inbox",
      analytics: "Analytics",
      leads: "Leads",
      knowledge: "Knowledge base",
      channels: "Channels",
      demo: "Demo mode",
    },
    dashboard: {
      title: "Chatbot dashboard",
      subtitle: "Real-time chatbot metrics and lead funnel",
      kpi: {
        activeConversations: "Active conversations",
        avgResponseTime: "Avg response time",
        conversionRate: "Conversion rate",
        totalLeads: "Total leads",
      },
      sections: {
        funnel: "Ads → Chat → Lead → Deal funnel",
        recentConversations: "Recent conversations",
        aiAcceptRate: "AI suggestion accept rate",
        channelBreakdown: "Per-channel performance",
      },
      empty: "No conversations yet",
    },
    analytics: {
      title: "Chatbot analytics",
      subtitle: "Deep insights and export",
      filters: { period: "Period", channel: "Channel", agent: "Agent" },
      charts: {
        dailyVolume: "Daily conversation volume",
        responseTime: "Response time histogram",
        intentBreakdown: "Intent breakdown",
        topFaq: "Top FAQ questions",
        aiHumanRatio: "AI vs human reply ratio",
        leadTimeline: "Lead conversion timeline",
      },
    },
    leads: {
      title: "Chatbot leads",
      subtitle: "All leads created via chatbot",
      columns: {
        contact: "Contact",
        intent: "Intent",
        channel: "Channel",
        amoStatus: "AmoCRM",
        createdAt: "Created",
        source: "Source",
      },
      amoStatus: {
        synced: "Synced",
        pending: "Pending",
        failed: "Failed",
        notSynced: "Not synced",
      },
    },
    funnel: { ad: "Ads", chat: "Chat", lead: "Lead", deal: "Deal" },
    intent: {
      FAQ: "FAQ",
      BUYER_INTENT: "Buyer intent",
      OBJECTION: "Objection",
      COMPLAINT: "Complaint",
      SMALL_TALK: "Small talk",
      OTHER: "Other",
    },
    demo: {
      title: "Meta App Review demo",
      banner: "Meta Reviewer Demo Mode — English UI",
      steps: {
        connect: "Connect Instagram",
        selectAccount: "Select Business account",
        inbox: "View Inbox (live messages)",
        reply: "Accept AI suggestion + Reply",
        verify: "Verify on Instagram app",
        comments: "Comment management",
      },
      next: "Next step",
      done: "Finish",
    },
  },
  errors: {
    generic: "Unexpected error",
    network: "Network error. Please try again",
    forbidden: "Access denied",
    notFound: "Not found",
    server: "Server error",
  },
  ai: {
    assistant: "AI assistant",
    ask: "Ask a question...",
    thinking: "Thinking...",
  },
  sales: {
    contacts: "Contacts",
    pipelines: "Pipelines",
    deals: "Deals",
    calls: "Calls",
    custdev: "CustDev",
    managers: "Managers",
    leaderboard: "Leaderboard",
    plans: "Plans",
    voronka: "Funnel audit",
    playbook: "Playbook",
    todayCalls: "Today's calls",
    activeDeals: "Active deals",
  },
  coach: {
    hub: "Hub",
    lessons: "Lessons",
    chat: "Chat",
    attendance: "Attendance",
    certificates: "Certificates",
    exam: "Voice Exam",
    examStart: "Start exam",
    history: "History",
    scenarios: "Scenarios",
    stats: "Stats",
  },
  settings: {
    general: "General",
    branding: "Branding",
    integrations: "Integrations",
    telegram: "Telegram",
    salesCriteria: "Sales criteria",
    aiCosts: "AI costs",
  },
};
